import { fetchClient } from './api';
import useLocalStorageState from 'use-local-storage-state';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useIsAuthenticated, useJwt } from './auth';
import { useLocation, useSearchParams } from 'react-router-dom';
import UiContext from '../state/UiContext';

const apiClient = fetchClient();

export const track = async (type, data) => {
  try {
    if (
      window.localStorage.getItem('jwt') > '""' ||
      window.localStorage.getItem('delegateToken') > '""'
    )
      await apiClient.post('/analytics/event', { type, data });
  } catch (e) {
    console.error(e);
  }
};

export const useSetUserProperties = (once = false) => {
  const [localUserProperties, setLocalUserProperties] = useLocalStorageState(
    once ? 'userPropertiesOnce' : 'userProperties',
    { defaultValue: {} },
  );
  const [jwt] = useJwt();
  useEffect(() => {
    if (jwt)
      apiClient.post('/analytics/set?' + (once ? 'once=true' : ''), { data: localUserProperties });
  }, [jwt, localUserProperties, once]);

  return useCallback(
    properties => {
      if (
        Object.keys(properties).some(
          k =>
            properties[k] !== undefined &&
            (once
              ? !Object.keys(localUserProperties).includes(k)
              : localUserProperties[k] !== properties[k]),
        )
      ) {
        setLocalUserProperties(properties);
      }
    },
    [localUserProperties, setLocalUserProperties, once],
  );
};

export const useLandingEffect = () => {
  const [isAuthenticated] = useIsAuthenticated();
  const trackedLanding = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const landingProperties = useMemo(() => {
    const splt = pathname.split('/');
    const props = {
      landing: pathname,
      landing1: splt[1] || '[none]',
      landing2: splt[2] || '[none]',
      landing3: splt[3] || '[none]',
      landing4: splt[4] || '[none]',
    };

    props.shared = !!searchParams.get('sharedBy');
    props.sharedBy = searchParams.get('sharedBy') || '[none]';

    props.linkCode = searchParams.get('link') || '[none]';

    props.outbrainClickId = searchParams.get('OutbrainClickId') || '';

    return props;
  }, [pathname, searchParams]);

  useEffect(() => {
    if (isAuthenticated && !trackedLanding.current) {
      track('land', landingProperties);
      const deletedParams = ['link', 'sharedBy', 'OutbrainClickId'];
      if (deletedParams.some(p => searchParams.get(p) !== undefined)) {
        deletedParams.forEach(p => searchParams.delete(p));
        setSearchParams(searchParams, { replace: true });
      }
      trackedLanding.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
};

export const useUserProperties = () => {
  const { isXs, isSm, isMd, isLg, isXl, isXxl } = useContext(UiContext);
  const setUserProperties = useSetUserProperties(false);

  const { pathname } = useLocation();

  useEffect(() => {
    setUserProperties({
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isXxl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
};
